import React, { createContext, useContext, useState } from "react";
import { ReferenceFormData } from "features/createApplication/components/ReferenceForm";
import { CoApplicantFormData } from "features/createApplication/components/CoApplicantForm";

interface MultiStepFormContextType {
  addressIds: string[];
  setAddressIds: (ids: string[]) => void;
  bankIds: string[];
  setBankIds: (ids: string[]) => void;
  references: ReferenceFormData[];
  setReferences: (references: ReferenceFormData[]) => void;
  coApplicants: CoApplicantFormData[];
  setCoApplicants: (coApplicants: CoApplicantFormData[]) => void;
  addCoApplicant: (coApplicant: CoApplicantFormData) => void;
  updateCoApplicant: (index: number, coApplicant: CoApplicantFormData) => void;
  removeCoApplicant: (index: number) => void;
  leadId: string | null;
  setLeadId: (id: string) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (step: number) => void;
  resetForm: () => void;
}

const MultiStepFormContext = createContext<MultiStepFormContextType | undefined>(undefined);

export const MultiStepFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [addressIds, setAddressIds] = useState<string[]>([]);
  const [bankIds, setBankIds] = useState<string[]>([]);
  const [references, setReferences] = useState<ReferenceFormData[]>([]);
  const [coApplicants, setCoApplicants] = useState<CoApplicantFormData[]>([]);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const goToNextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const goToPreviousStep = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  const resetForm = () => {
    setAddressIds([]);
    setBankIds([]);
    setReferences([]);
    setCoApplicants([]);
    setCurrentStep(0);
  };

  const addCoApplicant = (coApplicant: CoApplicantFormData) => {
    setCoApplicants((prev) => [...prev, coApplicant]);
  };

  const updateCoApplicant = (index: number, coApplicant: CoApplicantFormData) => {
    setCoApplicants((prev) => prev.map((c, i) => (i === index ? coApplicant : c)));
  };

  const removeCoApplicant = (index: number) => {
    setCoApplicants((prev) => prev.filter((_, i) => i !== index));
  };

  // For debugging
  console.log("MultiStepFormContext state:", { addressIds, bankIds, references, coApplicants, leadId, currentStep });

  return (
    <MultiStepFormContext.Provider value={{ 
      addressIds, 
      setAddressIds,
      bankIds,
      setBankIds,
      references,
      setReferences,
      coApplicants,
      setCoApplicants,
      addCoApplicant,
      updateCoApplicant,
      removeCoApplicant,
      leadId, 
      setLeadId,
      currentStep,
      setCurrentStep,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      resetForm
    }}>
      {children}
    </MultiStepFormContext.Provider>
  );
};

export const useMultiStepForm = () => {
  const context = useContext(MultiStepFormContext);
  if (context === undefined) {
    throw new Error("useMultiStepForm must be used within a MultiStepFormProvider");
  }
  return context;
};
